import { useMemo, useState } from "react";
import { AlertCircle, Info, RefreshCw, TrendingDown, TrendingUp, UserPlus, Users } from "lucide-react";
import { PageHeader } from "@/components/PageHeader";
import { PlanGate } from "@/components/billing/PlanGate";
import { Button } from "@/components/ui/button";
import { TrendLine } from "@/components/analytics/charts";
import { AnalyticsTabs, ChartSkeleton, EmptyChart, MetricCard, PeriodPicker, SectionCard, chartColor, fmt } from "@/components/analytics/shared";
import { useMemberAnalytics, useRequestAudienceSnapshot } from "@/lib/hooks/useMemberAnalytics";
import { resolveAnalyticsRange, shortDayLabel, type AnalyticsSelection, type MemberAnalytics } from "@/lib/analytics";
import { cn } from "@/lib/utils";

type PlatformFilter = "telegram" | "whatsapp";

function JoinsLeavesChart({ daily }: { readonly daily: MemberAnalytics["daily"] }): JSX.Element {
  const max = Math.max(1, ...daily.flatMap((d) => [d.joins, d.leaves]));
  if (!daily.some((d) => d.joins > 0 || d.leaves > 0)) {
    return <EmptyChart message="Nenhuma entrada ou saída registrada no período." />;
  }
  return (
    <div className="flex flex-col gap-2">
      <div className="flex h-32 items-end gap-[2px]" role="img" aria-label="Entradas e saídas por dia">
        {daily.map((d) => (
          <div key={d.day} className="flex h-full flex-1 flex-col justify-end gap-[1px]" title={`${shortDayLabel(d.day)}: +${d.joins} / -${d.leaves}`}>
            <div className="rounded-t-sm bg-[hsl(150_54%_51%)]" style={{ height: `${(d.joins / max) * 50}%` }} />
            <div className="rounded-b-sm bg-[hsl(0_70%_60%)]" style={{ height: `${(d.leaves / max) * 50}%` }} />
          </div>
        ))}
      </div>
      <div className="flex justify-between text-[10px] text-muted-foreground">
        <span>{shortDayLabel(daily[0]!.day)}</span>
        <span className="flex gap-3">
          <span className="flex items-center gap-1"><span className="h-2 w-2 rounded-full bg-[hsl(150_54%_51%)]" />Entradas</span>
          <span className="flex items-center gap-1"><span className="h-2 w-2 rounded-full bg-[hsl(0_70%_60%)]" />Saídas</span>
        </span>
        <span>{shortDayLabel(daily[daily.length - 1]!.day)}</span>
      </div>
    </div>
  );
}

function SubscribersChart({ data }: { readonly data: MemberAnalytics }): JSX.Element {
  const series = data.subscribers.filter((s) => s.points.length > 0);
  if (series.length === 0) {
    return <EmptyChart message="Nenhuma contagem de inscritos ainda. Use Atualizar para gravar a primeira." />;
  }
  return (
    <ul className="flex flex-col gap-4">
      {series.map((s, i) => {
        const last = s.points[s.points.length - 1]!;
        const first = s.points[0]!;
        return (
          <li key={s.chatId} className="flex flex-col gap-1">
            <div className="flex items-center justify-between text-xs">
              <span className="truncate font-medium text-foreground">{s.title ?? s.chatId}</span>
              <span className="font-mono tabular-nums text-muted-foreground">
                {fmt(last.count)} inscritos
                {s.points.length > 1 && ` (${last.count - first.count >= 0 ? "+" : ""}${fmt(last.count - first.count)})`}
              </span>
            </div>
            <TrendLine values={s.points.map((p) => p.count)} color={chartColor(i)} label={`Inscritos de ${s.title ?? s.chatId} por dia`} className="h-10" />
          </li>
        );
      })}
    </ul>
  );
}

function GroupsTable({ groups }: { readonly groups: MemberAnalytics["groups"] }): JSX.Element {
  if (groups.length === 0) {
    return <EmptyChart message="Nenhum grupo ou canal com dados. O bot precisa ser administrador do grupo." />;
  }
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-xs text-muted-foreground">
            <th scope="col" className="py-2 pr-3 font-medium">Grupo ou canal</th>
            <th scope="col" className="py-2 px-3 text-right font-medium">Entradas</th>
            <th scope="col" className="py-2 px-3 text-right font-medium">Saídas</th>
            <th scope="col" className="py-2 px-3 text-right font-medium">Líquido</th>
            <th scope="col" className="py-2 pl-3 text-right font-medium">Inscritos</th>
          </tr>
        </thead>
        <tbody>
          {groups.map((g) => {
            const net = g.joins - g.leaves;
            return (
              <tr key={g.chatId} className="border-t border-[hsl(var(--border))]">
                <td className="py-2 pr-3 text-foreground">{g.title ?? g.chatId}</td>
                <td className="py-2 px-3 text-right font-mono tabular-nums">{fmt(g.joins)}</td>
                <td className="py-2 px-3 text-right font-mono tabular-nums">{fmt(g.leaves)}</td>
                <td className={cn("py-2 px-3 text-right font-mono tabular-nums", net > 0 && "text-[hsl(var(--primary))]", net < 0 && "text-[hsl(var(--destructive))]")}>
                  {net > 0 ? "+" : ""}
                  {fmt(net)}
                </td>
                <td className="py-2 pl-3 text-right font-mono tabular-nums">{g.members === null ? "---" : fmt(g.members)}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function WhatsappLimitation(): JSX.Element {
  return (
    <SectionCard title="WhatsApp" icon={<Info size={14} />}>
      <p className="text-sm leading-relaxed text-muted-foreground">
        O WhatsApp não informa de forma confiável quem entra ou sai dos grupos para integrações como a nossa, nem a contagem de membros ao longo do tempo. Para não mostrar números inventados, esta visão fica só para o Telegram.
      </p>
      <p className="text-sm leading-relaxed text-muted-foreground">
        Para acompanhar a procura pelos seus grupos de WhatsApp, use o <a href="/app/rodizio" className="text-[hsl(var(--primary))] underline-offset-4 hover:underline">Rodízio de grupos</a>: ele conta os cliques de entrada em cada grupo.
      </p>
    </SectionCard>
  );
}

function TelegramView({ data, isLoading }: { readonly data: MemberAnalytics | undefined; readonly isLoading: boolean }): JSX.Element {
  return (
    <>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <MetricCard label="Entradas" value={fmt(data?.joins ?? 0)} icon={<UserPlus size={14} />} subtitle="no período" isLoading={isLoading} />
        <MetricCard label="Saídas" value={fmt(data?.leaves ?? 0)} icon={<TrendingDown size={14} />} subtitle="no período" isLoading={isLoading} />
        <MetricCard label="Crescimento líquido" value={`${(data?.net ?? 0) > 0 ? "+" : ""}${fmt(data?.net ?? 0)}`} icon={<TrendingUp size={14} />} subtitle="entradas menos saídas" isLoading={isLoading} />
        <MetricCard label="Grupos com dados" value={fmt(data?.groupsWithData ?? 0)} icon={<Users size={14} />} subtitle="com entrada ou saída" isLoading={isLoading} />
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
        <SectionCard title="Entradas x saídas" subtitle="por dia" className="lg:col-span-3">
          {isLoading || !data ? <ChartSkeleton /> : <JoinsLeavesChart daily={data.daily} />}
        </SectionCard>
        <SectionCard title="Inscritos dos canais" subtitle="contagem gravada a cada 6 horas e no Atualizar" className="lg:col-span-2">
          {isLoading || !data ? <ChartSkeleton /> : <SubscribersChart data={data} />}
        </SectionCard>
      </div>
      <SectionCard title="Por grupo e canal">{isLoading || !data ? <ChartSkeleton /> : <GroupsTable groups={data.groups} />}</SectionCard>
      <p className="text-xs text-muted-foreground">
        Entradas e saídas só são registradas onde o bot do LucraLink é administrador, a partir do momento em que ele entrou. Não guardamos quem entrou: só a contagem.
      </p>
    </>
  );
}

function MembrosPageContent(): JSX.Element {
  const [selection, setSelection] = useState<AnalyticsSelection>({ preset: "30d" });
  const [platform, setPlatform] = useState<PlatformFilter>("telegram");
  const range = useMemo(() => resolveAnalyticsRange(selection), [selection]);
  const { data, isLoading, error } = useMemberAnalytics(platform === "telegram" ? range : null);
  const refresh = useRequestAudienceSnapshot();

  return (
    <div className="space-y-6 max-w-6xl">
      <PageHeader
        title="Analytics"
        subtitle="Quem entra e sai dos seus grupos e canais."
        actions={<PeriodPicker value={selection} onChange={setSelection} invalid={range === null} />}
      />
      <AnalyticsTabs active="members" />
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div role="group" aria-label="Plataforma" className="flex rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--surface))] p-0.5">
          {(["telegram", "whatsapp"] as const).map((p) => (
            <button
              key={p}
              type="button"
              aria-pressed={platform === p}
              onClick={() => setPlatform(p)}
              className={cn("rounded-md px-3 py-1 text-xs font-medium", platform === p ? "bg-[hsl(var(--surface-raised))] text-foreground border border-[hsl(var(--border-strong))]" : "text-muted-foreground hover:text-foreground")}
            >
              {p === "telegram" ? "Telegram" : "WhatsApp"}
            </button>
          ))}
        </div>
        {platform === "telegram" && (
          <div className="flex items-center gap-3">
            {data?.lastSnapshotAt && <span className="text-xs text-muted-foreground">Última contagem: {new Date(data.lastSnapshotAt).toLocaleString("pt-BR")}</span>}
            <Button variant="secondary" size="sm" onClick={() => refresh.mutate()} disabled={refresh.isPending}>
              <RefreshCw size={14} className={cn(refresh.isPending && "animate-spin")} />
              Atualizar
            </Button>
          </div>
        )}
      </div>
      {refresh.isSuccess && <p role="status" className="text-xs text-[hsl(var(--primary))]">Pedido enviado. A contagem nova aparece em alguns segundos.</p>}
      {refresh.error && <p role="alert" className="text-xs text-[hsl(var(--destructive))]">{refresh.error.message}</p>}
      {error && (
        <div role="alert" className="flex items-center gap-3 rounded-lg border border-[hsl(var(--destructive)/0.3)] bg-[hsl(var(--destructive)/0.08)] px-4 py-3 text-sm text-[hsl(var(--destructive))]">
          <AlertCircle size={16} className="shrink-0" />
          <span>Não foi possível carregar membros e inscritos: {error.message}</span>
        </div>
      )}
      {platform === "telegram" ? <TelegramView data={data} isLoading={isLoading} /> : <WhatsappLimitation />}
    </div>
  );
}

/** Membros e inscritos: analytics de grupos e canais é PRO (gate central). */
export function MembrosPage(): JSX.Element {
  return (
    <PlanGate feature="fullAnalytics" title="Analytics" subtitle="Quem entra e sai dos seus grupos e canais.">
      <MembrosPageContent />
    </PlanGate>
  );
}
